import './SinglePageDetailsContainer.scss';
import { ContentLayout, SpaceBetween, Header, Container, Link, Button } from "@amzn/awsui-components-react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetComments, useGetNotes, useGetOrder, useGetOrderAttachments } from "hooks";
import { AMAZONIAN, AMZNHAM_CONSUMER, HAM_CONSUMER, ITSE } from "interfaces/constants";
import { useEffect, useState, useRef, useMemo } from "react";
import { getString } from "common";
import { useSinglePageSections } from "./hooks/useSinglePageSections";
import { useMessagesPanel } from "./hooks/useMessagesPanel";
import Paths from 'common/paths/paths';
import { hasOrderLabel, replaceTaxonomyWithOrderReference } from 'helpers/helpers';
import { OrderDetailsContentHeader } from '../OrderDetailsContentHeader';
import { UserPermissions } from "../../../services/user-permissions";
import { PERMISSIONS } from "../../../configuration/config";
import { GetCommentsCommandOutput, Relation } from "@amzn/ito-client";

// Constants
const stringPath = "orderDetails.singlePage";
const notesTitlePath = "messages.internalMessagesTitle";

const defaultTitlePath = "messages.title";
const amznhamMessagesTitlePath = "messages.amznhamTitle";

const phonetoolURL = "https://phonetool.amazon.com/users/";

/**
 * Renders the header description with requested by and on behalf of information
 */
const renderHeaderDescription = (data: any, stringPath: string) => {
  if (!data?.requested?.by) return null;

  return (
    <>
      {getString("requestedBy", stringPath)} <Link external href={`${phonetoolURL}${data.requested.by}`}>{data.requested.by}</Link>
      {data.requester?.userAlias && data.requester.userAlias !== data.requested.by && (
        <>
          {" - "}
          {getString("onBehalfOf", stringPath)} <Link external href={`${phonetoolURL}${data.requester.userAlias}`}>{data.requester.userAlias}</Link>
        </>
      )}
    </>
  );
};

/**
 * Props interface for the SinglePageDetailsContainer component
 * @interface SinglePageDetailsContainerProps
 * @property {any} useOrderEditItems - Hook for managing order edit items state
 * @property {any} useOrderEditActions - Hook for managing order edit actions
 * @property {any} [useFlashBarItemsActions] - Optional hook for managing flash bar notifications
 */
export interface SinglePageDetailsContainerProps {
  useOrderEditItems: any;
  useOrderEditActions: any;
  useFlashBarItemsActions?: any;
  showAuditsPanel?: (orderId: string | undefined, orderReference: string | undefined,
                     orderRelations: Relation[], consumer: string) => void;
  isItseMode?: boolean;
  isExternalView?: boolean;
}

export const SinglePageDetailsContainer = (props: SinglePageDetailsContainerProps) => {
  const navigate = useNavigate();
  const [showNotes, setShowNotes] = useState<boolean>(false);
  const [showComments, setShowComments] = useState<boolean>(false);
  const [messagesTitlePath, setMessagesTitlePath] = useState<string>(defaultTitlePath);
  const [isAmznham, setIsAmznham] = useState<boolean>(false);
  const [isItseOrder, setIsItseOrder] = useState<boolean>(false);
  const [commentsSource, setCommentsSource] = useState<string>("");
  const [showAuditTrails, setShowAuditTrails] = useState<boolean | undefined>(undefined);
  const preserveSelectionRef = useRef<boolean>(false);
  const hasInitiallyLoadedRef = useRef<boolean>(false);

  // Retrieve Order Id from URL params
  let { id } = useParams();

  if (id?.includes(`${AMZNHAM_CONSUMER}-`)) {
    id = id.slice(id.indexOf("-") + 1);
  }

  // Get data
  const { data, isLoading, error, doGetOrder } = useGetOrder(id);
  const amznhamOrder = useGetOrder(undefined);
  const { comments, isLoading: isLoadingComments, errorForGetComments, doGetComments, setComments } = useGetComments(undefined);
  const { notes, errorForGetNotes, doGetNotes, setNotes } = useGetNotes(undefined);

  // Feature flag
  // show the audit button only after loading the order data.
  useEffect(() => {
    if (showAuditTrails === undefined && data && data.orderReference) {
      setShowAuditTrails(UserPermissions.instance.hasPermission(PERMISSIONS.SHOW_AUDIT_TRAILS));
    }
  }, [data]);

  // Update URL to display orderReference instead of Taxonomy ID
  useEffect(() => {
    if (id?.startsWith("ito") && data?.orderReference) {
      replaceTaxonomyWithOrderReference(Paths.orderSinglePageView.url, data.orderReference);
    }
  }, [isLoading]);

  useEffect(() => {
    // Change message section title text to "Order support messages"
    setMessagesTitlePath(isAmznham ? amznhamMessagesTitlePath : defaultTitlePath);
    // Get AMZNHAM order to display orderReference, status and hopOrderId
    amznhamOrder.doGetOrder(data?.relations?.find(r => r.id?.includes(`${AMZNHAM_CONSUMER}`))?.id);
  }, [isAmznham]);

  // Attachments
  const attachmentHook = useGetOrderAttachments({ orderId: data?.orderId })
  useEffect(() => {
    attachmentHook.doGetAttachments()
  }, [data?.orderId]);

  // Display order id in breadcrumbs and get comments
  useEffect(() => {
    if (data && data.orderReference) {
      setIsAmznham(hasOrderLabel(data, AMAZONIAN));
      setIsItseOrder(hasOrderLabel(data, ITSE));
      if (data.requested?.via === AMZNHAM_CONSUMER) {
        // Redirect AMZNHAM order to HAM order
        const firstRelationId = data.relations?.[0]?.id;
        if (firstRelationId) {
          navigate(Paths.orderSinglePageView.url.slice(0, Paths.orderSinglePageView.url.lastIndexOf("/")) + "/" + encodeURIComponent(firstRelationId));
          navigate(0);
        }
      }

      const relationId = data?.relations?.find(r => r.id?.includes(`${AMZNHAM_CONSUMER}`))?.id;
      if (hasOrderLabel(data, AMAZONIAN) && relationId) {
        setCommentsSource(relationId);
      } else {
        setCommentsSource(data.orderId!);
      }

      props.useOrderEditActions.setCurrentOrderInformation(data);

      // When reloading the order, keep all previously selected items
      if (props.useOrderEditItems.selectedItems && props.useOrderEditItems.selectedItems.length > 0) {
        const previouslySelectedItems = props.useOrderEditItems.selectedItems.map((selectedItem: any) =>
          data.items?.find((item: any) => item.itemId === selectedItem.itemId)
        ).filter(Boolean);

        if (previouslySelectedItems.length > 0) {
          props.useOrderEditActions.setSelectedLineItems(previouslySelectedItems);
          preserveSelectionRef.current = true;
        }
      } else if (!preserveSelectionRef.current) {
        // Only reset if we haven't just preserved selections
        const previouslySelectedItem = data.items?.find(item => item.itemId === props.useOrderEditItems.lastLineItemSelected?.itemId);
        props.useOrderEditActions.setSelectedLineItems(previouslySelectedItem ? [previouslySelectedItem] : []);
      }

      doGetNotes({ taxonomyId: data!.orderId! });

      if (!hasInitiallyLoadedRef.current) {
        const timeoutId = setTimeout(() => window.scrollTo(0, 0), 100);
        hasInitiallyLoadedRef.current = true;
        return () => clearTimeout(timeoutId);
      }
    }
  }, [data]);

  useEffect(() => {
    if (commentsSource) {
      doGetComments({ taxonomyId: commentsSource });
    }
  }, [commentsSource]);

  // Reset the preserve flag when selections change
  useEffect(() => {
    preserveSelectionRef.current = false;
  }, [props.useOrderEditItems.selectedItems]);

  // Display error
  useEffect(() => {
    if (error && error.message) {
      props.useFlashBarItemsActions?.addErrorFlashBarItem(error.message);
    }
  }, [error]);

  // Display comments
  useEffect(() => {
    if (comments) {
      setShowComments(!errorForGetComments);
    }
    if (errorForGetComments && errorForGetComments.$metadata.httpStatusCode !== 403) {
      props.useFlashBarItemsActions?.addErrorFlashBarItem(errorForGetComments.message);
    }
  }, [comments, errorForGetComments]);

  // Display notes
  useEffect(() => {
    if (notes) {
      setShowNotes(!errorForGetNotes);
    }
    if (errorForGetNotes && errorForGetNotes.$metadata.httpStatusCode !== 403) {
      props.useFlashBarItemsActions?.addErrorFlashBarItem(errorForGetNotes.message);
    }
  }, [notes, errorForGetNotes]);

  // Refresh order details page when edited
  useEffect(() => {
    const fetchData = async () => {
      doGetOrder(id);
    }
    if (props.useOrderEditItems.editedOrderId && props.useOrderEditItems.editedOrderId !== "") {
      fetchData();
      props.useOrderEditActions.reloadOrder("");
    }
  }, [props.useOrderEditItems.editedOrderId]);

  // Last synced tracking for manual refresh
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  // Update lastSyncedAt whenever comments are fetched
  useEffect(() => {
    if (comments) {
      setLastSyncedAt(new Date());
    }
  }, [comments]);

  // Manual refresh handler
  const onRefresh = () => {
    if (commentsSource) {
      doGetComments({ taxonomyId: commentsSource });
    }
  };

  const collapsibleMessagesEnabled = UserPermissions.instance.hasPermission(PERMISSIONS.COLLAPSIBLE_MESSAGES);

  const lastComment = useMemo(() => {
    const typedComments = comments as GetCommentsCommandOutput | null;
    const first = typedComments?.comments?.[0];
    if (!first?.commentId) return null;
    return { commentId: first.commentId, postedBy: first.posted?.by ?? '' };
  }, [comments]);

  const messagesPanel = useMessagesPanel({
    featureEnabled: collapsibleMessagesEnabled,
    orderId: data?.orderId,
    currentUser: UserPermissions.instance.username,
    lastComment,
  });

  useEffect(() => {
    if (!commentsSource || !collapsibleMessagesEnabled || !messagesPanel.isMinimized) return;

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        doGetComments({ taxonomyId: commentsSource });
      }
    }, 300000); // 5 minutes

    return () => clearInterval(interval);
  }, [commentsSource, collapsibleMessagesEnabled, messagesPanel.isMinimized, doGetComments]);

  const {
    renderOrderSummarySection,
    renderItemsRequestedSection,
    renderSubLineItemsSection,
    renderMessagesSection,
    renderNotesSection
  } = useSinglePageSections({
    stringConfig: {
      stringPath,
      messagesTitlePath,
      notesTitlePath
    },
    orderData: {
      data,
      amznhamOrder: amznhamOrder.data,
      comments,
      notes
    },
    stateHandlers: {
      setComments,
      setNotes
    },
    displayOptions: {
      showComments,
      showNotes,
      notesError: !!errorForGetNotes,
      commentsError: !!errorForGetComments,
      isAmznham,
      isItseOrder,
      canComment: props.isItseMode ? isItseOrder : undefined,
      showAllSubLineItems: true
    },
    props,
    attachmentHook,
    commentsSource,
    onCommentsRefresh: onRefresh,
    isLoadingComments,
    messagesPanel,
    onRefresh,
    lastSyncedAt
  });

  const handleAuditTrailsClick = () => {
      if (props.showAuditsPanel) {
          props.showAuditsPanel(data?.orderId, data?.orderReference,
              data?.relations ?? [],
              isAmznham ? AMZNHAM_CONSUMER : HAM_CONSUMER);
      }
  };

  return (
    <ContentLayout
      header={
        <Header
          data-testid="order-details-page-header"
          variant="h1"
          description={renderHeaderDescription(data, stringPath)}
          actions={
            (showAuditTrails && !props.isExternalView &&
            <Button
              variant="primary"
              onClick={handleAuditTrailsClick}>
                {getString("auditTrails.button.showPanel")}
            </Button>)
          }
        >
            <OrderDetailsContentHeader data={data} error={error} isLoading={isLoading} stringPath={stringPath} isAmznham={isAmznham} amznhamOrder={amznhamOrder}  />
        </Header>
      }
    >
      {!isLoading && !error &&
        <div className={`single-page-container__grid${messagesPanel.isMinimized ? ' single-page-container__grid--minimized' : ''}${messagesPanel.isMaximized ? ' single-page-container__grid--maximized' : ''}`}>
          {/** Left side column - Order Details */}
          <div className="single-page-container__left-column">
            <SpaceBetween direction="vertical" size="m">
              {renderOrderSummarySection()}
              <Container>
                {renderItemsRequestedSection()}
                {renderSubLineItemsSection()}
              </Container>
            </SpaceBetween>
          </div>
          {/** Right side column - Messages and Notes */}
          {!messagesPanel.isMinimized && (
            <div className={`single-page-container__right-column${messagesPanel.isMaximized ? ' single-page-container__right-column--maximized' : ''}`}>
              <SpaceBetween direction="vertical" size="m">
                {(!messagesPanel.isMaximized || messagesPanel.maximizedPanel === 'messages') && renderMessagesSection()}
                {(!messagesPanel.isMaximized || messagesPanel.maximizedPanel === 'notes') && renderNotesSection()}
              </SpaceBetween>
            </div>
          )}
          {/** Floating button to restore messages when minimized */}
          {messagesPanel.isMinimized && (
            <div className="single-page-container__messages-fab">
              {messagesPanel.showBadge && <span className="single-page-container__messages-badge" data-testid="messages-badge" />}
              <Button
                iconName="contact"
                variant="primary"
                onClick={messagesPanel.restore}
                data-testid="messages-restore-button"
              >
                {getString("messages.showMessages", stringPath)}
              </Button>
            </div>
          )}
        </div >
      }
    </ContentLayout >
  );
}
